'use client'

import { useEffect, useRef, useState } from 'react'
import { AlertCircle, Loader2, RefreshCw, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import { trackTurnstileEvent } from '@/lib/turnstile-analytics'

type TurnstileInstance = {
  render: (
    container: string | HTMLElement,
    options: {
      sitekey: string
      action?: string
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => string
  reset: (id: string) => void
  remove: (id: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance
  }
}

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_SLOW_HINT_MS = 5000
const TURNSTILE_LOAD_TIMEOUT_MS = 30000
const TURNSTILE_RENDER_TIMEOUT_MS = 20000

type TurnstileLoadStatus =
  | 'loading'
  | 'rendering'
  | 'ready'
  | 'error'
  | 'verified'
type TurnstilePlacement = 'inline' | 'modal'

type TurnstileWidgetProps = {
  onVerify: (token: string) => void
  onExpire: () => void
  refreshKey: number
  placement?: TurnstilePlacement
}

export const TurnstileWidget = ({
  onVerify,
  onExpire,
  refreshKey,
  placement = 'modal',
}: TurnstileWidgetProps) => {
  const t = useTranslations('MarkdownToPdfConverter')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const [isScriptReady, setIsScriptReady] = useState(false)
  const [loadStatus, setLoadStatus] = useState<TurnstileLoadStatus>('loading')
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [showSlowHint, setShowSlowHint] = useState(false)

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return
    setLoadStatus('loading')
    setIsScriptReady(false)
    setShowSlowHint(false)

    let isDisposed = false
    let timeoutId: number | undefined
    let slowHintTimeoutId: number | undefined

    const clearLoadTimers = () => {
      if (typeof timeoutId === 'number') {
        window.clearTimeout(timeoutId)
        timeoutId = undefined
      }
      if (typeof slowHintTimeoutId === 'number') {
        window.clearTimeout(slowHintTimeoutId)
        slowHintTimeoutId = undefined
      }
    }

    const markReady = (trackLoaded: boolean) => {
      if (isDisposed) return
      clearLoadTimers()
      setShowSlowHint(false)
      setLoadStatus('rendering')
      setIsScriptReady(true)
      if (trackLoaded) {
        trackTurnstileEvent('turnstile_script_loaded', { placement })
      }
    }

    const markError = (reason: string) => {
      if (isDisposed) return
      clearLoadTimers()
      setLoadStatus('error')
      setIsScriptReady(false)
      trackTurnstileEvent('turnstile_script_error', { placement, reason })
    }

    timeoutId = window.setTimeout(() => {
      const script = document.getElementById(
        TURNSTILE_SCRIPT_ID,
      ) as HTMLScriptElement | null
      if (
        isDisposed ||
        script?.dataset.ready === 'true' ||
        typeof window.turnstile !== 'undefined'
      ) {
        return
      }

      setLoadStatus('error')
      setIsScriptReady(false)
      trackTurnstileEvent('turnstile_script_timeout', { placement })
    }, TURNSTILE_LOAD_TIMEOUT_MS)
    slowHintTimeoutId = window.setTimeout(() => {
      if (isDisposed) return
      setShowSlowHint(true)
    }, TURNSTILE_SLOW_HINT_MS)

    const existingScript = document.getElementById(
      TURNSTILE_SCRIPT_ID,
    ) as HTMLScriptElement | null

    const handleReady = () => markReady(true)
    const handleError = () => markError('network')

    if (existingScript) {
      if (
        existingScript.dataset.ready === 'true' ||
        existingScript.getAttribute('data-loaded') === 'true' ||
        (existingScript as any).readyState === 'complete' ||
        typeof window.turnstile !== 'undefined'
      ) {
        markReady(false)
      } else if (existingScript.dataset.error === 'true') {
        markError(existingScript.dataset.errorReason || 'previous_error')
      } else {
        existingScript.addEventListener('load', handleReady)
        existingScript.addEventListener('error', handleError)
      }
      return () => {
        isDisposed = true
        clearLoadTimers()
        existingScript.removeEventListener('load', handleReady)
        existingScript.removeEventListener('error', handleError)
      }
    }

    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => {
      script.dataset.ready = 'true'
      delete script.dataset.error
      delete script.dataset.errorReason
      markReady(true)
    }
    script.onerror = () => {
      script.dataset.error = 'true'
      script.dataset.errorReason = 'network'
      markError('network')
    }
    document.head.appendChild(script)

    return () => {
      isDisposed = true
      clearLoadTimers()
    }
  }, [loadAttempt, placement])

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !isScriptReady || !containerRef.current) return
    if (loadStatus !== 'rendering') return
    if (!window.turnstile) {
      setLoadStatus('error')
      trackTurnstileEvent('turnstile_script_error', {
        placement,
        reason: 'missing_global',
      })
      return
    }

    const container = containerRef.current
    let isDisposed = false
    let isWidgetRendered = false
    let renderTimeoutId: number | undefined
    let slowHintTimeoutId: number | undefined

    const clearRenderTimers = () => {
      if (typeof renderTimeoutId === 'number') {
        window.clearTimeout(renderTimeoutId)
        renderTimeoutId = undefined
      }
      if (typeof slowHintTimeoutId === 'number') {
        window.clearTimeout(slowHintTimeoutId)
        slowHintTimeoutId = undefined
      }
    }

    const hasRenderedWidget = () => Boolean(container.querySelector('iframe'))
    const removeRenderedWidget = () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }

    const markWidgetRendered = () => {
      if (isDisposed || isWidgetRendered || !hasRenderedWidget()) return
      isWidgetRendered = true
      clearRenderTimers()
      setShowSlowHint(false)
      setLoadStatus('ready')
      trackTurnstileEvent('turnstile_widget_rendered', { placement })
    }

    setShowSlowHint(false)

    const observer = new MutationObserver(markWidgetRendered)
    observer.observe(container, {
      attributes: true,
      childList: true,
      subtree: true,
    })

    slowHintTimeoutId = window.setTimeout(() => {
      if (isDisposed || isWidgetRendered) return
      setShowSlowHint(true)
    }, TURNSTILE_SLOW_HINT_MS)

    renderTimeoutId = window.setTimeout(() => {
      if (isDisposed || isWidgetRendered) return
      observer.disconnect()
      removeRenderedWidget()
      setLoadStatus('error')
      setShowSlowHint(false)
      trackTurnstileEvent('turnstile_widget_render_timeout', { placement })
      onExpire()
    }, TURNSTILE_RENDER_TIMEOUT_MS)

    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current)
      widgetIdRef.current = null
    }

    const siteKey = TURNSTILE_SITE_KEY
    const id = window.turnstile?.render(containerRef.current, {
      sitekey: siteKey,
      action: 'markdown-to-pdf',
      callback: (token: string) => {
        clearRenderTimers()
        observer.disconnect()
        setLoadStatus('verified')
        setShowSlowHint(false)
        trackTurnstileEvent('turnstile_token_received', { placement })
        onVerify(token)
      },
      'expired-callback': () => {
        setLoadStatus('ready')
        setShowSlowHint(false)
        trackTurnstileEvent('turnstile_token_expired', { placement })
        onExpire()
      },
      'error-callback': () => {
        clearRenderTimers()
        observer.disconnect()
        removeRenderedWidget()
        setLoadStatus('error')
        setShowSlowHint(false)
        trackTurnstileEvent('turnstile_widget_error', { placement })
        onExpire()
      },
    })

    if (id) {
      widgetIdRef.current = id
    }
    markWidgetRendered()

    return () => {
      isDisposed = true
      clearRenderTimers()
      observer.disconnect()
      removeRenderedWidget()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScriptReady, onVerify, onExpire, placement, refreshKey])

  const handleRetry = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.remove(widgetIdRef.current)
      widgetIdRef.current = null
    }

    const script = document.getElementById(TURNSTILE_SCRIPT_ID)
    if (
      script instanceof HTMLScriptElement &&
      script.dataset.ready !== 'true'
    ) {
      script.remove()
    }

    setLoadStatus('loading')
    setShowSlowHint(false)
    setIsScriptReady(false)
    trackTurnstileEvent('turnstile_retry', { placement })
    setLoadAttempt((value) => value + 1)
  }

  if (!TURNSTILE_SITE_KEY) {
    return (
      <div className="text-sm text-destructive">
        Cloudflare Turnstile site key is not configured.
      </div>
    )
  }

  if (loadStatus === 'error') {
    return (
      <div
        className="w-full max-w-md rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-center"
        role="alert"
      >
        <div className="flex items-center justify-center gap-2 text-sm font-medium text-destructive">
          <AlertCircle className="h-4 w-4" />
          <span>{t('verification.loadFailed')}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('verification.networkHint')}
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-3 gap-2"
          onClick={handleRetry}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          {t('verification.retry')}
        </Button>
      </div>
    )
  }

  const statusMessage =
    loadStatus === 'loading'
      ? t('verification.connecting')
      : loadStatus === 'verified'
        ? t('verification.verifiedGenerating')
        : t('verification.loading')
  const shouldShowStatusPanel =
    loadStatus === 'loading' ||
    loadStatus === 'rendering' ||
    loadStatus === 'verified'
  const shouldShowWidgetContainer =
    loadStatus === 'rendering' || loadStatus === 'ready'

  return (
    <div className="w-full max-w-md space-y-2" aria-live="polite">
      {shouldShowStatusPanel ? (
        <div
          key="turnstile-status"
          className="flex min-h-[72px] w-full items-center justify-center gap-3 rounded-md border border-dashed bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <div className="text-center">
            <div>{statusMessage}</div>
            {showSlowHint ? (
              <div className="mt-1 text-xs">
                {t('verification.loadingSlow')}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
      {shouldShowWidgetContainer ? (
        <div
          key="turnstile-widget-container"
          ref={containerRef}
          data-testid="turnstile-widget-container"
          className={
            loadStatus === 'ready'
              ? 'flex justify-center'
              : 'pointer-events-none h-0 overflow-hidden opacity-0'
          }
        />
      ) : null}
    </div>
  )
}

interface VerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onVerify: (token: string) => void
  onExpire: () => void
  refreshKey: number
}

export function VerificationModal({
  isOpen,
  onClose,
  onVerify,
  onExpire,
  refreshKey,
}: VerificationModalProps) {
  const t = useTranslations('MarkdownToPdfConverter')

  useEffect(() => {
    if (!isOpen) return
    trackTurnstileEvent('turnstile_modal_open', { placement: 'modal' })
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 duration-200 animate-in fade-in zoom-in-95">
      <div className="relative w-full max-w-[340px] rounded-lg bg-background p-6 shadow-lg">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <div className="flex flex-col items-center gap-4">
          <div className="text-center font-semibold">
            {t('verification.title') || 'Verification Required'}
          </div>
          <div className="text-center text-sm text-muted-foreground">
            {t('verification.description') ||
              'Please complete the check to continue download.'}
          </div>
          <TurnstileWidget
            onVerify={onVerify}
            onExpire={onExpire}
            refreshKey={refreshKey}
            placement="modal"
          />
        </div>
      </div>
    </div>
  )
}
